<script>
  import { getContext, onDestroy, onMount } from "svelte";
  import { SelectRectangle } from "../../utils/selectRectangle";
  import ChartContainer from "./ChartContainer.svelte";
  import TimelineArea from "./TimelineArea.svelte";
  import GraphArea from "./GraphArea.svelte";
  import ResGanttMenuBar from "../../views/generic/ResGantt/ResGanttMenuBar.svelte";
  import DataFetcher from "./DataFetcher.js";
  import { translate } from "../../i18n/i18next.js";
  import { getNotificationsContext } from "svelte-notifications";
  import OrderGanttMenuBar from "../../views/generic/OrderGantt/OrderGanttMenuBar.svelte";
  import { useProject } from "../../hooks/useProject";
  import { getTimeStringSec } from "../../utils/time.js";
  import {useTimezone} from "../../hooks/useTimezone.js";

  export let boxDimensions = "width: 100vw; height: 100vh;";

  // Gantt store imports
  export let GanttStore

  const {
    GanttChartType,
    IsAutoUpdateEnabled,
    ProjectId,
    TransactionsUrl,
    MetadataUrl,
    LastUploadTimestamp,
    ShouldAskConfirmationBeforeReloadingForAutoUpdate,
  } = GanttStore

  const {handleFetchErrors} = getContext("fetchErrorHandler")

  const {addNotification} = getNotificationsContext();

  const project = useProject($ProjectId, handleFetchErrors);
  const {getOffset} = useTimezone();

  let lastDownloadTimestamp = null;
  let autoUpdateInterval = null;
  let autoUpdateProjectInterval = null;
  let consecutiveRetries = 0;
  let consecutiveRetriesProject = 0;
  const maxRetries = 10;

  function removeAutoUpdateInterval() {
    if (autoUpdateInterval !== null) {
      clearInterval(autoUpdateInterval);
      autoUpdateInterval = null;
    }
  }

  function removeAutoUpdateProjectInterval() {
    if (autoUpdateProjectInterval !== null) {
      clearInterval(autoUpdateProjectInterval);
      autoUpdateProjectInterval = null;
    }
  }

  const _updateSuccess = (newTransactions) => {
    if (newTransactions !== null) {
      consecutiveRetries = 0;
      GanttStore.Transactions.update((old) => {
        let trs = old;
        for (const key of Object.keys(newTransactions)) {
          trs[key] = newTransactions[key];
        }
        GanttStore.LastTransactionDownloadTimestamp.update((_) => newTransactions.DownloadedTimeStamp);
        return trs;
      });
    }
  }

  const _updateError = () => {
    consecutiveRetries += 1;
    addNotification({
      text: translate("frontend:ganttChart.autoUpdate.messages.error"),
      position: "top-right",
      removeAfter: 4000,
      type: "error",
    });
    if (consecutiveRetries >= maxRetries) {
      addNotification({
        text: translate("frontend:ganttChart.autoUpdate.messages.maxRetryExceeded"),
        position: "top-right",
        removeAfter: 4000,
        type: "error",
      });
      consecutiveRetries = 0;
      removeAutoUpdateInterval();
      IsAutoUpdateEnabled.set(false);
    }
  }

  const _updateErrorMetadata = () => {
    consecutiveRetriesProject += 1;
    addNotification({
      text: translate("frontend:ganttChart.autoUpdate.messages.error"),
      position: "top-right",
      removeAfter: 4000,
      type: "error",
    });
    if (consecutiveRetriesProject >= maxRetries) {
      addNotification({
        text: translate("frontend:ganttChart.autoUpdate.messages.maxRetryExceeded"),
        position: "top-right",
        removeAfter: 4000,
        type: "error",
      });
      consecutiveRetriesProject = 0;
      removeAutoUpdateProjectInterval();
    }
  }

  let haveAskedForConfirmation = false
  let oldMetadata = null
  const _compareMetadata = (newMetadata) => {
    if (!oldMetadata) {
      oldMetadata = newMetadata
      return
    }

    // compare the old and new metadata's timestamps
    // meta.json's "generated_at" property includes offset information like "2025-01-23T21:52:31+0900".
    // by constructing Date object convert it to UTC.
    const oldMetaDataTimeStringInUTC = new Date(oldMetadata.generated_at).toISOString()
    const newMetaDataTimeStringInUTC = new Date(newMetadata.generated_at).toISOString()

    oldMetadata = newMetadata

    if (newMetaDataTimeStringInUTC > oldMetaDataTimeStringInUTC) {
      console.log(`generated at (old, new): ${oldMetaDataTimeStringInUTC}, ${newMetaDataTimeStringInUTC}`)
      console.log("seems that new data has been uploaded.")
      if (!$ShouldAskConfirmationBeforeReloadingForAutoUpdate) return location.reload()
      if (!haveAskedForConfirmation && confirm(translate("frontend:ganttChart.autoUpdate.messages.projectUpdated"))) {
        return location.reload()
      }
      haveAskedForConfirmation = true
    }
  }

  const UpdateTransactions = () => {
    // Delete here if not resource gantt
    if (!$IsAutoUpdateEnabled
      || ($GanttChartType !== GanttStore.GanttChartTypes.ResGantt && $GanttChartType !== GanttStore.GanttChartTypes.CustomizedResGantt)) {
      IsAutoUpdateEnabled.set(false);
      removeAutoUpdateInterval();
      return;
    }

    if ($TransactionsUrl === null
      || lastDownloadTimestamp === null
      || document.hidden === true
    ) return;

    DataFetcher.FetchTransactionDataAsync($TransactionsUrl, lastDownloadTimestamp, _updateSuccess, _updateError);
  }

  const UpdateMetadata = () => {
    // Delete here if not resource gantt
    if (!$IsAutoUpdateEnabled
      || ($GanttChartType !== GanttStore.GanttChartTypes.ResGantt && $GanttChartType !== GanttStore.GanttChartTypes.CustomizedResGantt)) {
      removeAutoUpdateProjectInterval();
      return;
    }

    if ($MetadataUrl === null
      || document.hidden === true
    ) return;

    DataFetcher.FetchMetadataAsync($MetadataUrl, _compareMetadata, _updateErrorMetadata);
  }

  onMount(async () => {
    if (
      $GanttChartType == GanttStore.GanttChartTypes.ResGantt ||
      $GanttChartType == GanttStore.GanttChartTypes.SimpleResGantt ||
      $GanttChartType == GanttStore.GanttChartTypes.CustomizedResGantt ||
      $GanttChartType == GanttStore.GanttChartTypes.OrderGantt ||
      $GanttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt
    ) {
      // Init select rectangle api
      SelectRectangle.AttachToDiv(
        document.getElementById("gantt_select-rectangle")
      );


      SelectRectangle.AttachSingleCallFunction((arr) => {

        const newArr = arr.map((el) => {
          let _newEl = [];
          _newEl.push(el); // Ope object id

          // Resource object id
          const _domEl = document.getElementById(el);
          let _row;
          if ($GanttChartType != GanttStore.GanttChartTypes.OrderGantt && $GanttChartType != GanttStore.GanttChartTypes.PeggedOrderGantt)
            _row = _domEl.classList.value.match(/r_(\d+)/)[1];
          else
            _row = _domEl.classList.value.match(/o_(\d+)/)[1];
          _newEl.push(_row);

          return _newEl;
        })

        GanttStore.TimelineItems.set(newArr);
      });

      // Used for forced operations (select without using the select rectangle)
      SelectRectangle.AttachSingleCallFunctionNoDOM((ids, data) => {
        if (ids.length === 0) return;

        const newArr = [[ids[0], data]];
        GanttStore.TimelineItems.set(newArr);
      });
    }

    // auto updates
    GanttStore.LastTransactionDownloadTimestamp.subscribe(
      (ldt) => {
        lastDownloadTimestamp = ldt;
    });

    if ($IsAutoUpdateEnabled === true) {
      autoUpdateInterval = setInterval(UpdateTransactions, 10000);

      // fetch the current metadata
      UpdateMetadata()
      // schedule refetching
      autoUpdateProjectInterval = setInterval(UpdateMetadata, 10000);
    } else {
      removeAutoUpdateInterval();
      removeAutoUpdateProjectInterval()
    }

  });

  onDestroy(() => {
    removeAutoUpdateInterval();
    removeAutoUpdateProjectInterval()
  })
</script>

<div id="gantt_select-rectangle" />
<div id="gantt" style={boxDimensions}>
  <div id="gantt_main">
    {#if $GanttChartType == GanttStore.GanttChartTypes.ResGantt
      || $GanttChartType == GanttStore.GanttChartTypes.SimpleResGantt
      || $GanttChartType == GanttStore.GanttChartTypes.CustomizedResGantt
    }
      <ResGanttMenuBar
        GanttStore={GanttStore}
      />
    {:else}
      <OrderGanttMenuBar
        GanttStore={GanttStore}
      />
    {/if}
    <ChartContainer
      GanttStore={GanttStore}
    />
    {#if $GanttChartType == GanttStore.GanttChartTypes.ResGantt || $GanttChartType == GanttStore.GanttChartTypes.SimpleResGantt || $GanttChartType == GanttStore.GanttChartTypes.CustomizedResGantt}
      <GraphArea
        GanttStore={GanttStore}
      />
    {/if}
  </div>
  <TimelineArea
    GanttStore={GanttStore}
    project={$ProjectId}
  />
</div>

<style>
  div#gantt {
    user-select: none;
    display: flex;
    flex-direction: row;
    background-color: #f0f0f0;
  }

  div#gantt_main {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
</style>
